import React, { useState, useMemo, useEffect } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  ExpandedState,
  Row,
  HeaderContext,
} from '@tanstack/react-table';
import { ChevronUp, Inbox } from 'lucide-react';

import { cn, formatNumberWithCommas, getProfitLossClass } from '@/utils';
import Text from '@/utils/responsiveText';
import { DataTableSkeleton } from '../ui/DataTableskeleton';

type Align = 'left' | 'center' | 'right';

type MyMeta = {
  align?: Align;
};

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  // ... other props unchanged
  onSelectionChange?: (selectedRows: TData[]) => void;
  enableSelection?: boolean;
  enableAccordion?: boolean;
  pageSize?: number;
  variant?: 'default' | 'primary' | 'secondary';
  isLoading?: boolean;
  renderSubRow?: (rowData: TData) => React.ReactNode;
  accordionPosition?: number;
  headerClassName?: string;
  rowvariant?: 'default' | 'green';
  total?: boolean;
  totals?: Record<string, any>;
  rows?: number;
  rowClassNames?: string;
}

export function DataTable<TData>({
  data,
  columns,
  onSelectionChange,
  enableSelection = false,
  headerClassName,
  enableAccordion = false,
  pageSize = 10,
  variant = 'default',
  rowvariant = 'default',
  total,
  totals,
  isLoading,
  renderSubRow,
  accordionPosition,
  rows = 10,
  rowClassNames,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    setExpanded({});
  }, [data]);

  const tableColumns = useMemo(() => {
    const baseColumns = enableSelection
      ? [
          {
            id: 'select',
            header: ({ table }: HeaderContext<TData, unknown>) => (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={table.getIsAllPageRowsSelected()}
                onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
              />
            ),
            cell: ({ row }: { row: Row<TData> }) => (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={row.getIsSelected()}
                onChange={(e) => row.toggleSelected(e.target.checked)}
              />
            ),
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : [];

    if (!enableAccordion) return [...baseColumns, ...columns];

    const expandColumn: ColumnDef<TData> = {
      id: 'expander',
      header: () => null,
      cell: ({ row }: { row: Row<TData> }) =>
        row.getCanExpand() ? (
          <button
            {...{
              onClick: row.getToggleExpandedHandler(),
              style: { cursor: 'pointer' },
              'aria-label': row.getIsExpanded() ? 'Collapse' : 'Expand',
              className: 'p-1',
            }}
          >
            <div className="bg-green3 rounded-full text-gold-foreground">
              <ChevronUp
                size={20}
                className={`${row.getIsExpanded() ? '' : 'rotate-180'} transition-all duration-300`}
              />
            </div>
          </button>
        ) : null,
      enableSorting: false,
      enableHiding: false,
    };

    const fullCols = [...baseColumns, ...columns];
    const pos = accordionPosition ?? baseColumns.length;
    fullCols.splice(pos, 0, expandColumn);

    return fullCols;
  }, [columns, enableSelection, enableAccordion, accordionPosition]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      expanded,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    getRowCanExpand: () => Boolean(enableAccordion),
  });

  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, table]);

  const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

  const headerVariant = {
    default: 'bg-raised text-text',
    primary: 'bg-navy text-white',
    secondary: 'text-text',
  };

  const rowVariant = {
    default: 'bg-panel hover:bg-raised',
    green: 'bg-gold/5 hover:bg-raised',
  };

  const visibleColumnCount = table.getVisibleFlatColumns().length;

  return (
    <div className="space-y-2 relative">
      <div className="border border-border rounded-t-xl max-h-[510px] overflow-x-auto">
        <table className="min-w-max w-full table-auto">
          <thead className="border-b border-border rounded-xl bg-tableHead ">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = (header.column.columnDef.meta as MyMeta | undefined)?.align;
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'h-12 px-2.5 font-semibold text-[11px] uppercase tracking-wide text-left',
                        align === 'center' && 'text-center',
                        align === 'right' && 'text-right',
                        headerVariant[variant],
                        headerClassName,
                        header.index === 0 && 'rounded-tl-xl',
                        header.index === headerGroup.headers.length - 1 && 'rounded-tr-xl'
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'inline-flex items-center gap-2 w-full',
                            align === 'center' && 'justify-center',
                            align === 'right' && 'justify-end',
                            header.column.getCanSort() && 'cursor-pointer select-none'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              <DataTableSkeleton columns={table.getVisibleLeafColumns().length} rows={rows} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={`main_${row.id}`}>
                  <tr
                    key={`row_${row.id}`}
                    className={cn(
                      `border-b border-border transition-colors text-left ${rowClassNames}`,
                      rowVariant[rowvariant],
                      row.getIsSelected() && 'bg-raised'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={`cell_${cell.id}`}
                        className="px-2.5 py-1.5 align-middle text-xs text-text"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr key={`sub_${row.id}`}>
                      <td colSpan={visibleColumnCount} className="border-b border-border p-0">
                        <div className="w-full overflow-x-auto">
                          <div style={{ width: '100%' }}>
                            <div
                              style={{
                                width: '100%',
                              }}
                              className="p-1"
                            >
                              {renderSubRow ? renderSubRow(row.original) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumnCount} className="py-14 text-center">
                  <div className="mx-auto mb-3 grid size-14 place-items-center rounded-full bg-gold/15">
                    <Inbox className="h-7 w-7 text-gold" aria-hidden="true" />
                  </div>
                  <Text min={15} max={18} as="h3" className="font-semibold text-text">
                    No data available
                  </Text>
                  <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                    Nothing to show here yet. Try changing the filters or date range.
                  </p>
                </td>
              </tr>
            )}
          </tbody>

          {total && totals && table.getRowModel().rows?.length !== 0 && (
            <tfoot>
              <tr className="bg-navy text-[13px] border-t border-border">
                {table.getVisibleLeafColumns().map((column, index) => {
                  const key = column.id;
                  const value = totals ? totals[key] : undefined;

                  return (
                    <td
                      key={key}
                      className={`px-4 py-2 text-white  text-center ${getProfitLossClass(value)}`}
                    >
                      {index === 0 ? (
                        <span className="">Total</span>
                      ) : value !== undefined ? (
                        formatNumberWithCommas(value)
                      ) : (
                        ''
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
export default DataTable;
